import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { QueueNo, UrgentBadge } from "@/components/Shared";

const n = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Mirrors Dashboard.jsx's own STAGE_FOR_STATUS (kept local — this tile is a generic, reusable KPI
// shape, not wired into that file's per-widget config).
const STAGE_FOR_STATUS = {
  in_production: "printing", partially_printed: "printing",
  printing: "avt", flat_bed: "avt", avt: "slitting", slitting: "quality",
  qc: "packing", packing: "coa", ready_to_dispatch: "dispatch",
};

/** A compact KPI tile (same shape as PartialDispatch) for "N jobs in this status" — click it for the
 * full list in a dialog. `linkable=false` makes the dialog's rows plain text, not links: for a
 * look-ahead status this role may only ever list, never open (the server enforces that regardless;
 * this just keeps the dialog from offering a link that would bounce). */
export default function JobStatusTile({ status, title, icon: Icon, testId, linkable = true }) {
  const [total, setTotal] = useState(null);
  const [notInPipeline, setNotInPipeline] = useState(0);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  // A combined status ("printing,flat_bed") still lands on one stage sheet — every status feeding
  // this tile belongs to the same desk, so the first one's stage is the right one for all of them.
  const stage = STAGE_FOR_STATUS[status.split(",")[0]];
  const load = () => {
    // `ready`: these statuses plus the Converted BPR jobs whose BPR Status puts them at this desk.
    api.get("/jobs", { params: { ready: status, limit: 1, with_total: true } }).then(({ data }) => { setTotal(data.total); setNotInPipeline(data.counts?.not_in_pipeline || 0); }).catch(() => setTotal((t) => t ?? 0));
  };
  const loadRows = () => {
    // The department's queue order from the server (urgent first, then the operations head's order), each
    // job with its number in the queue — the whole queue, so the dialog matches the count on the tile.
    api.get("/jobs", { params: { ready: status, limit: 500 } })
      .then(({ data }) => setRows(data))
      .catch(() => setRows((r) => r || []));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) loadRows(); }, [open, status]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("jobs", () => { load(); if (open) loadRows(); });

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testId || `kpi-${status}`}
        className="block h-full w-full text-left border border-neutral-200 bg-white px-3 py-2.5 md:px-4 md:py-3 hover:border-[#FF5A00] transition-colors">
        <div className="flex items-start justify-between">
          <div className="label-caps text-[10px] leading-tight">{title}</div>
          {Icon && <Icon size={13} strokeWidth={1.75} className="text-neutral-400 shrink-0" />}
        </div>
        <div className="font-mono-num text-lg md:text-xl font-bold mt-1.5 tracking-tight text-neutral-900">{total == null ? "—" : n(total)}</div>
        {notInPipeline > 0 && total != null && (
          <div className="text-[10px] text-neutral-500 mt-0.5" data-testid={`${testId || status}-split`}>{n(total - notInPipeline)} in Pipeline + {n(notInPipeline)} not in Pipeline</div>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid={`${testId || status}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">{title} · {n(total)}</DialogTitle>
            {!linkable && <DialogDescription>For awareness only — these aren't open to work on yet.</DialogDescription>}
          </DialogHeader>
          {rows == null && <div className="py-8 text-center text-sm text-neutral-400">Loading…</div>}
          {rows?.length === 0 && <div className="py-8 text-center text-sm text-neutral-400">Nothing in this state.</div>}
          {rows && rows.length > 0 && (
            <div className="divide-y divide-neutral-100 border border-neutral-200">
              {rows.map((j) => {
                const Row = linkable ? Link : "div";
                const rowProps = linkable ? { to: stage ? `/orders/${j.id}?tab=production_flow&stage=${stage}` : `/orders/${j.id}`, onClick: () => setOpen(false) } : {};
                return (
                  <Row key={j.id} {...rowProps} className={`px-3 py-2.5 flex justify-between items-center ${linkable ? "hover:bg-neutral-50" : ""} ${j.urgent_schedule ? "bg-rose-50/70" : ""}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="w-9 text-right"><QueueNo n={j.queue_no} /></span>
                      <UrgentBadge urgent={!!j.urgent_schedule} />
                      <div>
                        <div className="font-mono-num text-xs font-semibold whitespace-nowrap">{j.mpwo_number || j.job_number}</div>
                        <div className="text-sm">{j.customer_name}</div>
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500 truncate max-w-[45%] text-right">{j.product_name}</div>
                  </Row>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
